import {
  ControlledDateInput,
  ControlledInput,
} from "components/Card/ControlledInput";
import FutureMedicalCard from "components/Card/FutureMedicalCard";
import { ControlledTextArea, MaskedInput, TextInput } from "components/inputs";
import type {
  Control,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import type { ConstantTypes } from "types/DataTypes";
import SectionBreak from "../../../components/SectionBreak";
import { getPartTypeTitle } from "../lien-utils";
import type {
  ArgumentTypeChoices,
  Lien,
  LienArgument,
  LienFormValues,
} from "../LienTypes";
import ArgumentsSection from "./part-type-sections/ArgumentsSection";
import CPLSection from "./part-type-sections/CPLSection";
import CPNSection from "./part-type-sections/CPNSection";
import DemandSection from "./part-type-sections/part-ab/DemandSection";
import HearingRequestSection from "./part-type-sections/part-ab/HearingRequestSection";
import JudicialReviewSection from "./part-type-sections/part-ab/JudicialReviewSection";
import MACAppealSection from "./part-type-sections/part-ab/MACAppealSection";
import ReconsiderationSection from "./part-type-sections/part-ab/ReconsiderationSection";
import RequestToReopenSection from "./part-type-sections/part-ab/request-to-reopen/RequestToReopenSection";
import RequestToVacateSection from "./part-type-sections/part-ab/request-to-vacate/RequestToVacateSection";
import DirectoryCompanySection from "./part-type-sections/part-cd/DirectoryCompanySection";
import FinalLienRequestSection from "./part-type-sections/part-cd/FinalLienRequestSection";
import InitialLienRequestSection from "./part-type-sections/part-cd/InitialLienRequestSection";
import SecondLienRequestSection from "./part-type-sections/part-cd/SecondLienRequestSection";

type PartTypeInformationSectionProps = {
  lien: Lien;
  control: Control<LienFormValues>;
  getValues: UseFormGetValues<LienFormValues>;
  setValue: UseFormSetValue<LienFormValues>;
  handleFieldChange: (field: string | null, value: unknown) => Promise<void>;
  lienArguments?: LienArgument[];
  constants?: ConstantTypes & {
    ArgumentTypeChoices?: ArgumentTypeChoices[];
  };
  accessToken: string;
};

const PartTypeInformationSection = ({
  lien,
  control,
  getValues,
  setValue,
  handleFieldChange,
  lienArguments,
  constants,
  accessToken,
}: PartTypeInformationSectionProps) => {
  const isPartAB = lien.part_type === "AB";
  const isPartCD = lien.part_type === "C" || lien.part_type === "D";

  const getCaseNumberLabel = () => {
    return isPartCD ? "Plan Name" : "Case ID";
  };

  return (
    <div className="swim-column">
      <FutureMedicalCard
        heading={`${getPartTypeTitle(lien.part_type)} Information`}
      >
        <ControlledInput
          control={control}
          name="case_number"
          label={getCaseNumberLabel()}
          errors={{}}
          onSave={(value) => handleFieldChange("case_number", value)}
          componentType={TextInput}
          maxLength={50}
        />

        <ControlledDateInput
          control={control}
          name="date_of_injury"
          label="Date of Injury"
          errors={{}}
          onSave={(value) => handleFieldChange("date_of_injury", value)}
        />

        <ControlledInput
          control={control}
          name="rre_name"
          label="RRE Name"
          errors={{}}
          onSave={(value) => handleFieldChange("rre_name", value)}
          componentType={TextInput}
          maxLength={255}
        />

        {isPartCD && (
          <>
            <ControlledInput
              control={control}
              name="subrogation_agent_name"
              label="Subrogation Agent Name"
              errors={{}}
              onSave={(value) =>
                handleFieldChange("subrogation_agent_name", value)
              }
              componentType={TextInput}
            />
            <ControlledTextArea
              control={control}
              name="subrogation_agent_contact_information"
              label="Subrogation Agent Contact Information"
              advancedEdit={true}
              maxLength={2000}
              errors={{}}
              onSave={(value) =>
                handleFieldChange(
                  "subrogation_agent_contact_information",
                  value,
                )
              }
            />
          </>
        )}

        {isPartAB && (
          <ControlledInput
            control={control}
            name="current_conditional_payment_amount"
            label="Current Conditional Payment Amount"
            errors={{}}
            onSave={(value) =>
              handleFieldChange("current_conditional_payment_amount", value)
            }
            componentType={MaskedInput}
            mask="dollar"
          />
        )}

        <ControlledDateInput
          control={control}
          name="date_responded_to_client"
          label="Date Responded to Client"
          errors={{}}
          onSave={(value) =>
            handleFieldChange("date_responded_to_client", value)
          }
        />

        {isPartAB && (
          <>
            <ControlledDateInput
              control={control}
              name="date_of_closure"
              label="Date of Closure"
              errors={{}}
              onSave={(value) => handleFieldChange("date_of_closure", value)}
            />
            <ControlledDateInput
              control={control}
              name="lien_verification_received_on"
              label="Lien Verification Received On"
              errors={{}}
              onSave={(value) =>
                handleFieldChange("lien_verification_received_on", value)
              }
            />
          </>
        )}

        {isPartCD && (
          <ControlledDateInput
            control={control}
            name="lien_verification_received_on"
            label="Lien Verification Received On"
            errors={{}}
            onSave={(value) =>
              handleFieldChange("lien_verification_received_on", value)
            }
          />
        )}

        <SectionBreak />

        {isPartCD && (
          <>
            <InitialLienRequestSection
              control={control}
              getValues={getValues}
              setValue={setValue}
              handleFieldChange={handleFieldChange}
              constants={constants}
            />

            <SectionBreak />

            <SecondLienRequestSection
              control={control}
              getValues={getValues}
              setValue={setValue}
              handleFieldChange={handleFieldChange}
              constants={constants}
            />

            <SectionBreak />

            <FinalLienRequestSection
              control={control}
              getValues={getValues}
              setValue={setValue}
              handleFieldChange={handleFieldChange}
              constants={constants}
            />

            <SectionBreak />

            <DirectoryCompanySection
              handleFieldChange={handleFieldChange}
              currentDirectoryCompany={lien.directory_company}
            />
          </>
        )}

        {isPartAB && (
          <>
            <CPLSection
              control={control}
              getValues={getValues}
              setValue={setValue}
              handleFieldChange={handleFieldChange}
              constants={constants}
            />

            <SectionBreak />

            <CPNSection
              control={control}
              getValues={getValues}
              setValue={setValue}
              handleFieldChange={handleFieldChange}
              constants={constants}
            />

            <SectionBreak />

            <DemandSection
              control={control}
              getValues={getValues}
              setValue={setValue}
              constants={constants}
              handleFieldChange={handleFieldChange}
            />

            <SectionBreak />

            <RequestToVacateSection
              lienId={lien.id}
              organizationId={lien.organization}
              accessToken={accessToken}
              requestToVacateAvailable={lien.request_to_vacate_available}
            />
            {/* request to vacate has it's own section break, since it's hidden if not available */}

            <ReconsiderationSection
              control={control}
              getValues={getValues}
              setValue={setValue}
              constants={constants}
              handleFieldChange={handleFieldChange}
            />

            <SectionBreak />

            <HearingRequestSection
              control={control}
              getValues={getValues}
              setValue={setValue}
              handleFieldChange={handleFieldChange}
            />

            <SectionBreak />

            <MACAppealSection
              control={control}
              getValues={getValues}
              setValue={setValue}
              handleFieldChange={handleFieldChange}
              constants={constants}
            />

            <SectionBreak />

            <JudicialReviewSection
              control={control}
              getValues={getValues}
              setValue={setValue}
              handleFieldChange={handleFieldChange}
            />

            <SectionBreak />

            <RequestToReopenSection
              lienId={lien.id}
              organizationId={lien.organization}
              accessToken={accessToken}
            />
          </>
        )}

        <SectionBreak />

        <ArgumentsSection
          control={control}
          lienArguments={lienArguments}
          constants={constants}
          accessToken={accessToken}
          total_conditional_payment_by_argument_type={
            lien.total_conditional_payment_by_argument_type
          }
          setValue={setValue}
        />
      </FutureMedicalCard>
    </div>
  );
};

export default PartTypeInformationSection;
